import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useBookings } from "PFCore/hooks/queries";
import { useActivities } from "PFCore/hooks/queries/activities/use_activities";
import { Activity, Booking, PermissionRule } from "PFTypes";

type UseReassignData = {
  bookingIds: number[];
};
type UseReassignDataReturn = {
  bookings: Omit<Booking, "activity">[];
  activities: Activity[];
  isLoading: boolean;
};

export const useReassignData = ({ bookingIds }: UseReassignData): UseReassignDataReturn => {
  const canReassignBookingsMatches = useIsCurrentUserPermittedTo(PermissionRule.ReassignBookingsMatches);
  const canReassignBookingsNamedResource = useIsCurrentUserPermittedTo(
    PermissionRule.ReassignBookingsNamedResource
  );
  const canReassign = canReassignBookingsMatches || canReassignBookingsNamedResource;

  const { data: bookingsData, isFetching: isBookingsFetching } = useBookings(
    { id: uniq(bookingIds) },
    { enabled: canReassign && !isEmpty(bookingIds) }
  );

  const bookingActivityIds = uniq(
    compact((bookingsData?.entries ?? []).map(({ activity_id: activityId }) => activityId))
  );
  const { data: activitiesData, isFetching: isActivitiesFetching } = useActivities(
    {
      filters: {
        fields: {
          id: bookingActivityIds
        }
      },
      perPage: PER_PAGE_UNPAGINATED
    },
    {
      enabled: canReassign && !isEmpty(bookingActivityIds),
      retry: false
    }
  );

  const bookings = bookingsData?.entries ?? [];
  const activities = (activitiesData?.entries ?? []).map(({ activity }) => activity);
  const isLoading = isBookingsFetching || isActivitiesFetching;
  return {
    bookings,
    activities,
    isLoading
  };
};
